import { X } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * A panel that slides in from the right edge, over the page — or, with
 * `side="bottom"`, up from the bottom edge (the bell's container below `sm`).
 *
 * For a task that belongs beside the screen it was started from rather than in
 * front of it — configuring an export of the list still visible behind it. The
 * page stays readable; the sheet is dismissible with Escape, the backdrop, or
 * its close button, and focus is held inside while it is open and handed back
 * to whatever opened it when it closes (WCAG 2.4.3).
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'right',
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Pinned below the scrolling body — the sheet's primary action. */
  footer?: ReactNode;
  /** Edge the panel is anchored to. `bottom` adds rounded top corners and a grab handle. */
  side?: 'right' | 'bottom';
  className?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Focus the first control rather than the close button: the close button is
    // the one thing the person did not open the sheet to use.
    const first = panel?.querySelector<HTMLElement>(`[data-autofocus],${FOCUSABLE}`);
    first?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const head = items[0]!;
      const tail = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(28,36,52,0.38)] motion-safe:animate-in motion-safe:fade-in-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        className={cn(
          side === 'bottom'
            ? 'absolute inset-x-0 bottom-0 flex max-h-[85dvh] w-full flex-col rounded-t-2xl border-t bg-card shadow-[var(--shadow-modal)]'
            : 'absolute inset-y-0 right-0 flex w-full max-w-[460px] flex-col border-l bg-card shadow-[var(--shadow-modal)]',
          side === 'bottom'
            ? 'motion-safe:animate-in motion-safe:slide-in-from-bottom motion-safe:duration-200'
            : 'motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200',
          className,
        )}
      >
        {side === 'bottom' ? (
          <div data-sheet-handle aria-hidden="true" className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border" />
        ) : null}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b px-6 py-4">
          <div className="min-w-0">
            <h2 id="sheet-title" className="text-[16px] font-bold text-secondary-foreground">
              {title}
            </h2>
            {description ? <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 grow overflow-y-auto px-6 py-5">{children}</div>
        {footer ? <div className="shrink-0 border-t bg-[var(--subtle)] px-6 py-4">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
